import { useQuery } from "@tanstack/react-query";
import { Entry } from "contentful";
import { useInView } from "framer-motion";
import { createInfiniteHitsSessionStorageCache } from "instantsearch.js/es/lib/infiniteHitsCache";
import { useAtomValue } from "jotai";
import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteHits, useInstantSearch } from "react-instantsearch";

import { sequentialLoadingState } from "~/atoms/sequential-loading";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useConfiguration } from "~/contexts/configuration";
import { usePageTracked } from "~/contexts/page-trackings";
import { useUserTokenAlgolia } from "~/hooks/use-user-token-algolia";
import { convertProductFromAgoliaToEva, getDiscountedAndOOSProductsByIds } from "~/services/algolia-utils";
import Algolia from "~/types/algolia";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import TrackingUtils from "~/utils/tracking-utils";

import SkeletonProductTile from "../common/skeletons/skeleton-product-tile";
import CategoryFilters from "./category-filters";
import EditorialElementPLP from "./editorial-element/editorial-element-plp";
import styles from "./product-grid.module.scss";
import ProductTileAlgolia from "./product-tile-algolia";

const ProductTile = dynamic(() => import("./product-tile"), {
  loading: () => <SkeletonProductTile />,
});

type Props = {
  components?: Entry<Editorials.GridElement>[];
  listScope: string;
  categoryId: string;
  initialResultsHits: any[];
  algoliaUserToken: string;
  isPaginationMode?: boolean;
  staticProducts?: any[];
  onTotalChange?: (total: number) => void;
};

export default function AlgoliaProductListing(props: Props) {
  const infiniteHitsSessionStorage = useMemo(() => createInfiniteHitsSessionStorageCache(), []);
  useUserTokenAlgolia({ token: props.algoliaUserToken, categoryId: props.categoryId });
  const sequentialLoadingReady = useAtomValue(sequentialLoadingState);
  const breakpoint = useBreakpoint();

  const [path, setPath] = useState<string | undefined>(undefined);
  const {
    items: hits,
    isLastPage,
    showMore,
    sendEvent,
    isFirstPage,
    showPrevious,
    results,
  } = useInfiniteHits({ cache: infiniteHitsSessionStorage });
  const { configuration, eva } = useConfiguration();
  const { status } = useInstantSearch();

  const lastTrackedIndex = useRef<number>(0);
  const isPageTracked = usePageTracked();

  let countEditorialElement = 0;

  useEffect(() => {
    const clearSessionStorage = () => {
      sessionStorage.removeItem("ais.infiniteHits");
    };

    window.addEventListener("pagehide", clearSessionStorage);

    return () => {
      window.removeEventListener("pagehide", clearSessionStorage);
    };
  }, []);

  const productsToDisplay = useMemo(
    () => (props.isPaginationMode ? props.staticProducts || [] : hits),
    [props.isPaginationMode, props.staticProducts, hits]
  );

  const productsWithEditorialElement: (Algolia.Product | Entry<Editorials.GridElement>)[] | undefined = useMemo(
    () =>
      contentfulUtils.mergeProductsWithEditorials<Algolia.Product>(
        productsToDisplay as Algolia.Product[],
        props.components
      ),
    [productsToDisplay, props.components]
  );

  const { data: discountedAndOOSProducts } = useQuery<Algolia.DiscountedAndOOSProducts>({
    queryKey: ["algoliaProductListingAvailabilityAndDiscounts", productsToDisplay.map((hit) => hit.code)],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: async () => {
      return (
        (await getDiscountedAndOOSProductsByIds(
          eva,
          productsToDisplay.map((hit) => hit.objectID)
        )) ?? {}
      );
    },
    enabled: productsToDisplay.length > 0,
  });

  useEffect(() => {
    if (results?.nbHits !== undefined && props.onTotalChange) {
      props.onTotalChange(results.nbHits);
    }
  }, [results?.nbHits]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newPath = window.location.href;

    setPath(newPath);
    if (path && path !== newPath) {
      lastTrackedIndex.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  useEffect(() => {
    if (isPageTracked) {
      if (hits) {
        lastTrackedIndex.current = TrackingUtils.trackAlgoliaProductsListImpression(
          hits as Algolia.Product[],
          lastTrackedIndex.current,
          props.listScope,
          {
            currency: configuration.CurrencyID,
          }
        );
      }
    } else {
      lastTrackedIndex.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageTracked, hits, lastTrackedIndex.current]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const sentinelRef2 = useRef<HTMLDivElement>(null);
  const sentinelInView = useInView(sentinelRef, { margin: "0% 0% 50% 0%" });
  const sentinel2InView = useInView(sentinelRef2, { margin: "0% 0% -50% 0%" });
  const stickySentinelInView = useInView(sentinelRef, { margin: "0px 0px -44px 0px" });

  useEffect(() => {
    const usableNetContainer = document.getElementById(Constants.USABLENET_CONTAINER_KEY);

    if (usableNetContainer && breakpoint == "mobile" && sentinelRef.current) {
      if (!stickySentinelInView && sentinelRef.current.getBoundingClientRect().top > 0) {
        usableNetContainer.style.bottom = "65px";
      } else {
        usableNetContainer.style.bottom = "";
      }
    }

    return () => {
      if (usableNetContainer) {
        usableNetContainer.style.bottom = "";
      }
    };
  }, [sequentialLoadingReady.usablenet, breakpoint, stickySentinelInView]);

  // handle infinite scroll - show previous page
  useEffect(() => {
    if (props.isPaginationMode) return; // Disable infinite scroll in pagination mode

    if (sentinel2InView && !isFirstPage) {
      showPrevious();
    }
  }, [isFirstPage, sentinel2InView, showPrevious, props.isPaginationMode]);

  // handle infinite scroll
  useEffect(() => {
    if (props.isPaginationMode) return; // Disable infinite scroll in pagination mode

    if (sentinelInView && !isLastPage) {
      showMore();
    }
  }, [isLastPage, sentinelInView, showMore, props.isPaginationMode]);

  const clickEvent = async (algoliaProduct: Algolia.Product) => {
    sendEvent?.("click", algoliaProduct, "Hit Clicked");
    if ("__queryID" in algoliaProduct) {
      // save it in localstorage for cross-tabs navigation and set it as default on session
      localStorage.setItem("algolia_queryid", algoliaProduct.__queryID as string);
      sessionStorage.setItem("algolia_queryid", algoliaProduct.__queryID as string);
    }
  };

  return (
    <>
      {/* Always in DOM so IntersectionObserver is attached on mount; showMore effects guard isPaginationMode */}
      <div className={styles.sentinelTop} ref={sentinelRef2} aria-hidden="true" />
      {/* To avoid cumulative layout shift */}
      {!props.isPaginationMode &&
        hits.length === 0 &&
        status !== "idle" &&
        props.initialResultsHits.map((product: any) => (
          <div key={product.objectID + "-initial"} className={styles.tile}>
            <ProductTileAlgolia product={product} />
          </div>
        ))}
      {productsWithEditorialElement?.map((item, idx) => {
        if (contentfulUtils.isContentful(item)) {
          return (
            <EditorialElementPLP
              editorialElement={item}
              key={item.sys.id}
              countEditorialElement={++countEditorialElement}
            />
          );
        } else {
          return (
            <div key={item.objectID} className={styles.tile}>
              <ProductTile
                product={convertProductFromAgoliaToEva(item, configuration.CurrencyID)}
                idxInList={idx}
                listScope={props.listScope}
                noLazy={idx < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP}
                algolia={{ product: item, discountedAndOOSProducts, events: { clickEvent } }}
              />
            </div>
          );
        }
      })}
      {/* Always in DOM so IntersectionObserver is attached on mount; showMore effects guard isPaginationMode */}
      <div className={styles.sentinelBottom} ref={sentinelRef} aria-hidden="true" />
      <div className={styles.filters}>
        <CategoryFilters pageType="inPLPAlgolia" />
      </div>
    </>
  );
}
